import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { mantineTheme } from './mantineTheme';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import JobDetail from './pages/JobDetail';
import NewJob from './pages/NewJob';
import ConverterDemo from './pages/ConverterDemo';
import Converter from './pages/Converter';
import ConverterNewJob from './pages/ConverterNewJob';
import ConverterJobDetail from './pages/ConverterJobDetail';
import ConverterFeatureGate from './components/converter/ConverterFeatureGate';
import SharedDashboardAccess from './components/SharedDashboardAccess';
import LegalPage from './pages/LegalPage';
import Onboarding from './components/Onboarding';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminOverview from './components/admin/AdminOverview';
import AdminUsage from './components/admin/AdminUsage';
import AdminCliOperationDetail from './components/admin/AdminCliOperationDetail';
import AdminWebJobDetail from './components/admin/AdminWebJobDetail';
import AdminApiRequestDetail from './components/admin/AdminApiRequestDetail';
import AdminErrors from './components/admin/AdminErrors';
import AdminInstances from './components/admin/AdminInstances';
import AdminInstanceBilling from './components/admin/AdminInstanceBilling';
import AdminJobs from './components/admin/AdminJobs';
import AdminApiKeys from './components/admin/AdminApiKeys';
import AdminAuditLog from './components/admin/AdminAuditLog';
import AdminContactRequests from './components/admin/AdminContactRequests';
import AdminHealth from './components/admin/AdminHealth';
import FeaturesSection from './components/admin/FeaturesSection';
import PricingSection from './components/admin/PricingSection';
import ConverterDemoTab from './components/admin/ConverterDemoTab';
import { NotificationPermission } from './components/NotificationPermission';
import { apiService } from './services/api';
import { DashboardView } from './features/dashboard/DashboardView';
import AdminRoute from './components/AdminRoute';
import MagicLinkVerify from './pages/MagicLinkVerify';
import AuthPage from './pages/AuthPage';
import Settings from './pages/Settings';
import Usage from './pages/Usage';
import SavedInstanceGate from './components/SavedInstanceGate';
import CliOperationDetail from './pages/CliOperationDetail';
import WebJobDetail from './pages/WebJobDetail';
import ApiRequestDetail from './pages/ApiRequestDetail';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  const [isRegistered, setIsRegistered] = useState<boolean | null>(null);

  // Feature flag for converter demo
  const showConverterDemo = process.env.REACT_APP_SHOW_CONVERTER_DEMO === 'true';

  useEffect(() => {
    // Check if already registered
    setIsRegistered(apiService.isRegistered());
  }, []);

  // index.html carries the public title. Signed-in pages use the short one.
  useEffect(() => {
    if (isRegistered) document.title = 'Alchemist';
  }, [isRegistered]);

  // Parse URL parameters on mount and store referral source
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');

    if (ref && ref.trim()) {
      // Store referral source in localStorage
      localStorage.setItem('referral_source', ref.trim());
    }
  }, []);

  // Send referral to backend if registered and has stored ref
  useEffect(() => {
    const sendReferral = async () => {
      const refSource = localStorage.getItem('referral_source');

      if (isRegistered && refSource) {
        try {
          await apiService.addReferral(refSource);
          // Clear from localStorage after successful send
          localStorage.removeItem('referral_source');
        } catch (error) {
          console.error('Failed to send referral:', error);
          // Keep in localStorage to retry later
        }
      }
    };

    sendReferral();
  }, [isRegistered]);

  const handleOnboardingComplete = () => {
    setIsRegistered(true);
  };

  if (isRegistered === null) {
    // Still checking
    return <div className="min-h-screen flex items-center justify-center bg-body-bg text-body-color">Loading...</div>;
  }

  // Standalone legal pages (no registration needed)
  if (
    window.location.pathname === '/privacy-policy' ||
    window.location.pathname === '/terms-of-use'
  ) {
    return (
      <MantineProvider defaultColorScheme="dark" theme={mantineTheme}>
        <QueryClientProvider client={queryClient}>
          <Router>
            <Routes>
              <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
              <Route path="/terms-of-use" element={<LegalPage type="terms" />} />
            </Routes>
          </Router>
        </QueryClientProvider>
      </MantineProvider>
    );
  }

  // Special case for shared dashboard routes (no registration needed)
  if (window.location.pathname.startsWith('/share/dashboard/')) {
    return (
      <MantineProvider defaultColorScheme="dark" theme={mantineTheme}>
        <QueryClientProvider client={queryClient}>
          <Router>
            <Routes>
              <Route path="/share/dashboard/:token" element={
                <Layout fullWidthContent={true} navigationMode="home-only">
                  <SharedDashboardAccess />
                </Layout>
              } />
            </Routes>
          </Router>
        </QueryClientProvider>
      </MantineProvider>
    );
  }

  return (
    <MantineProvider defaultColorScheme="dark" theme={mantineTheme}>
      <QueryClientProvider client={queryClient}>
        <Router>
          {!isRegistered ? (
            <Routes>
              <Route path="/auth/verify" element={<MagicLinkVerify />} />
              {/* key forces a remount so form state doesn't leak between modes */}
              <Route path="/signin" element={<AuthPage key="signin" mode="signin" />} />
              <Route path="/signup" element={<AuthPage key="signup" mode="signup" />} />
              <Route path="*" element={<Onboarding onComplete={handleOnboardingComplete} />} />
            </Routes>
          ) : (
            <>
              <NotificationPermission />
              <Routes>
                {/* Auth pages (also reachable while connected to an instance) */}
                <Route path="/auth/verify" element={<MagicLinkVerify />} />
                <Route path="/signin" element={<AuthPage key="signin" mode="signin" />} />
                <Route path="/signup" element={<AuthPage key="signup" mode="signup" />} />

                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }>
                  <Route index element={<Navigate to="/admin/overview" replace />} />
                  <Route path="overview" element={<AdminOverview />} />
                  <Route path="usage" element={<AdminUsage />} />
                  <Route path="usage/cli/operations/:id" element={<AdminCliOperationDetail />} />
                  <Route path="usage/web/jobs/:id" element={<AdminWebJobDetail />} />
                  <Route path="usage/api/requests/:id" element={<AdminApiRequestDetail />} />
                  <Route path="errors" element={<AdminErrors />} />
                  <Route path="instances" element={<AdminInstances />} />
                  <Route path="instances/:id/billing" element={<AdminInstanceBilling />} />
                  <Route path="jobs" element={<AdminJobs />} />
                  <Route path="api-keys" element={<AdminApiKeys />} />
                  <Route path="audit-log" element={<AdminAuditLog />} />
                  <Route path="contact-requests" element={<AdminContactRequests />} />
                  <Route path="health" element={<AdminHealth />} />
                  <Route path="features" element={<FeaturesSection />} />
                  <Route path="pricing" element={<PricingSection />} />
                  <Route path="converter-demo" element={<ConverterDemoTab />} />
                </Route>
                
                {/* Dashboard route with full width content */}
                <Route path="/jobs/:jobId/dashboard" element={
                  <Layout fullWidthContent={true}>
                    <DashboardView />
                  </Layout>
                } />
                
                {/* Converter feature pages (gated by per-instance converter features) */}
                <Route path="/converter" element={
                  <Layout>
                    <ConverterFeatureGate require="any">
                      <Converter />
                    </ConverterFeatureGate>
                  </Layout>
                } />
                <Route path="/converter/jobs/new" element={
                  <Layout>
                    <ConverterFeatureGate require="web">
                      <ConverterNewJob />
                    </ConverterFeatureGate>
                  </Layout>
                } />
                <Route path="/converter/jobs/:id" element={
                  <Layout>
                    <ConverterFeatureGate require="web">
                      <ConverterJobDetail />
                    </ConverterFeatureGate>
                  </Layout>
                } />

                {/* Usage pages (saved instances only) */}
                <Route path="/usage" element={
                  <Layout>
                    <SavedInstanceGate>
                      <Usage />
                    </SavedInstanceGate>
                  </Layout>
                } />
                <Route path="/usage/cli/operations/:id" element={
                  <Layout>
                    <SavedInstanceGate>
                      <CliOperationDetail />
                    </SavedInstanceGate>
                  </Layout>
                } />
                <Route path="/usage/web/jobs/:id" element={
                  <Layout>
                    <SavedInstanceGate>
                      <WebJobDetail />
                    </SavedInstanceGate>
                  </Layout>
                } />
                <Route path="/usage/api/requests/:id" element={
                  <Layout>
                    <SavedInstanceGate>
                      <ApiRequestDetail />
                    </SavedInstanceGate>
                  </Layout>
                } />

                {/* Converter Demo route with full width content - only if feature flag is enabled */}
                {showConverterDemo && (
                  <Route path="/converter-demo" element={
                    <Layout fullWidthContent={true}>
                      <ConverterDemo />
                    </Layout>
                  } />
                )}
                
                {/* All other routes with standard layout */}
                <Route path="*" element={
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/jobs/new" element={<NewJob />} />
                      <Route path="/jobs/:id" element={<JobDetail />} />
                      <Route path="/settings" element={<Settings />} />
                    </Routes>
                  </Layout>
                } />
              </Routes>
            </>
          )}
        </Router>
      </QueryClientProvider>
    </MantineProvider>
  );
}

export default App;
